import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { pdfTheme } from '../../utils/pdfTheme';

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 9, fontFamily: 'Helvetica' },

  // School header
  schoolHeader: { textAlign: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: `3px solid ${pdfTheme.colors.primary}` },
  schoolLogo: { width: 60, height: 60, marginBottom: 6, alignSelf: 'center', objectFit: 'contain' },
  schoolName: { fontSize: 16, fontWeight: 'bold', color: pdfTheme.colors.primary, textTransform: 'uppercase', marginBottom: 3 },
  schoolMotto: { fontSize: 9, fontStyle: 'italic', marginBottom: 2 },
  schoolContact: { fontSize: 8, color: pdfTheme.colors.text, marginBottom: 1 },
  docTitle: { fontSize: 13, fontWeight: 'bold', color: pdfTheme.colors.primary, textTransform: 'uppercase', letterSpacing: 1, marginTop: 8, marginBottom: 2 },
  generatedDate: { fontSize: 8, color: pdfTheme.colors.text },

  // Meta
  meta: { flexDirection: 'row', gap: 16, marginBottom: 10, flexWrap: 'wrap' },
  metaItem: { fontSize: 8, color: pdfTheme.colors.text },
  metaVal: { fontWeight: 'bold', color: '#333' },

  // Summary bar
  summaryBar: { backgroundColor: pdfTheme.colors.primaryLight, padding: '8 12', marginBottom: 12, borderLeft: `4px solid ${pdfTheme.colors.primary}`, borderRadius: 2 },
  summaryText: { fontSize: 9, color: pdfTheme.colors.primary, fontWeight: 'bold' },

  // Table
  table: { width: '100%', border: `1px solid ${pdfTheme.colors.border}` },
  thead: { flexDirection: 'row', backgroundColor: pdfTheme.colors.primary, padding: '6 8' },
  row: { flexDirection: 'row', borderBottom: `1px solid ${pdfTheme.colors.border}`, padding: '5 8' },
  rowAlt: { backgroundColor: '#fafafa' },
  th: { fontSize: 8, fontWeight: 'bold', color: pdfTheme.colors.white },
  td: { fontSize: 8 },
  c0: { width: '5%' },
  c1: { width: '28%' },
  c2: { width: '14%' },
  c3: { width: '13%' },
  c4: { width: '8%' },
  c5: { width: '12%', textAlign: 'center' },
  c6: { width: '8%', textAlign: 'center' },
  c7: { width: '8%', textAlign: 'center' },
  c8: { width: '14%' },

  // Footer
  footer: { position: 'absolute', bottom: 20, left: 30, right: 30, textAlign: 'center', borderTop: `2px solid #e9ecef`, paddingTop: 6 },
  footerText: { fontSize: 7, color: '#95a5a6', marginBottom: 1 },
  footerBrand: { fontWeight: 'bold', color: pdfTheme.colors.primary },
});

const MarksListPdfDoc = ({ students, marks, configData, title, school }) => {
  const maxMarks = configData?.max_marks ?? '';
  const weight = configData?.weight_percentage ?? '';
  const className = configData?.class_stream_subject_paper?.class?.name || '';
  const stream = configData?.class_stream_subject_paper?.stream?.name || '';
  const subject = configData?.class_stream_subject_paper?.subject_paper?.subject?.subject_name || '';
  const assessmentName = configData?.assessment_type?.assessment_name || '';

  const markedCount = students.filter((s) => {
    const m = marks[s.id];
    return m && m.marks_obtained != null && m.marks_obtained !== '';
  }).length;

  const address = [school?.address, school?.city, school?.district, school?.country].filter(Boolean).join(', ');
  const contact = [school?.phone, school?.email, school?.website].filter(Boolean).join(' | ');

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="landscape">

        {/* School Header */}
        <View style={styles.schoolHeader}>
          {school?.logo && <Image src={school.logo} style={styles.schoolLogo} />}
          <Text style={styles.schoolName}>{school?.name || 'School Name'}</Text>
          {school?.motto && <Text style={styles.schoolMotto}>"{school.motto}"</Text>}
          {address ? <Text style={styles.schoolContact}>{address}</Text> : null}
          {contact ? <Text style={styles.schoolContact}>{contact}</Text> : null}
          <Text style={styles.docTitle}>Student Marks List</Text>
          <Text style={styles.generatedDate}>Generated on {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Text>
        </View>

        {/* Meta info */}
        <View style={styles.meta}>
          {!!className && <Text style={styles.metaItem}>Class: <Text style={styles.metaVal}>{className}{stream ? ` - ${stream}` : ''}</Text></Text>}
          {!!subject && <Text style={styles.metaItem}>Subject: <Text style={styles.metaVal}>{subject}</Text></Text>}
          {!!assessmentName && <Text style={styles.metaItem}>Assessment: <Text style={styles.metaVal}>{assessmentName}</Text></Text>}
          {maxMarks !== '' && <Text style={styles.metaItem}>Max Marks: <Text style={styles.metaVal}>{maxMarks}</Text></Text>}
          {weight !== '' && <Text style={styles.metaItem}>Weight: <Text style={styles.metaVal}>{weight}%</Text></Text>}
        </View>

        {/* Table */}
        <View style={styles.table}>
          <View style={styles.thead}>
            <Text style={[styles.th, styles.c0]}>#</Text>
            <Text style={[styles.th, styles.c1]}>Student Name</Text>
            <Text style={[styles.th, styles.c2]}>Reg No</Text>
            <Text style={[styles.th, styles.c3]}>Class</Text>
            <Text style={[styles.th, styles.c4]}>Gender</Text>
            <Text style={[styles.th, styles.c5]}>Marks{maxMarks !== '' ? ` (/${maxMarks})` : ''}</Text>
            <Text style={[styles.th, styles.c6]}>Absent</Text>
            <Text style={[styles.th, styles.c7]}>Exempt</Text>
            <Text style={[styles.th, styles.c8]}>Remarks</Text>
          </View>
          {students.map((s, i) => {
            const m = marks[s.id] || {};
            const marksVal = m.is_absent ? 'ABS' : m.is_exempted ? 'EXM' : (m.marks_obtained !== '' && m.marks_obtained != null ? String(Math.round(Number(m.marks_obtained))) : '-');
            return (
              <View key={s.id} style={[styles.row, i % 2 === 1 ? styles.rowAlt : {}]} wrap={false}>
                <Text style={[styles.td, styles.c0]}>{i + 1}</Text>
                <Text style={[styles.td, styles.c1]}>{s.full_name || `${s.last_name || ''} ${s.first_name || ''}`.trim()}</Text>
                <Text style={[styles.td, styles.c2]}>{s.registration_no || '-'}</Text>
                <Text style={[styles.td, styles.c3]}>{s.class_stream?.school_class?.name || '-'}</Text>
                <Text style={[styles.td, styles.c4, { textTransform: 'capitalize' }]}>{s.gender || '-'}</Text>
                <Text style={[styles.td, styles.c5]}>{marksVal}</Text>
                <Text style={[styles.td, styles.c6]}>{m.is_absent ? 'Yes' : 'No'}</Text>
                <Text style={[styles.td, styles.c7]}>{m.is_exempted ? 'Yes' : 'No'}</Text>
                <Text style={[styles.td, styles.c8]}>{m.remarks || '-'}</Text>
              </View>
            );
          })}
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Generated from <Text style={styles.footerBrand}>School Sync</Text></Text>
          <Text style={styles.footerText}>This is a computer-generated document. No signature is required.</Text>
          <Text style={styles.footerText}>© {new Date().getFullYear()} School Sync. All rights reserved.</Text>
        </View>

      </Page>
    </Document>
  );
};

export default MarksListPdfDoc;
